import { createContext, type FC, useMemo } from "react";
import {
    type ComponentType,
    type DragDropImageLoadCallback,
    DragDropImageLoadContext,
    type DragDropItemMap,
    isColumnComponentType,
} from "./DragDropTypes";
import { ColumnComponent } from "./InternalComponents/Columns/ColumnComponent";
import { ImageComponent } from "./InternalComponents/Image/ImageComponent";
import { SpacerComponent } from "./InternalComponents/Spacer/SpacerComponent";
import { TextComponent } from "./InternalComponents/Text/TextComponent";
import { TextReplacement } from "./InternalComponents/Text/TextReplacement";

const DEFAULT_ITEMS: DragDropItemMap = {
    text: {
        component: TextComponent,
    },
    img: {
        component: ImageComponent,
    },
    column: {
        component: ColumnComponent,
    },
    spacer: {
        component: SpacerComponent,
    },
};

type DragDropDefaultItems = keyof typeof DEFAULT_ITEMS;

interface DragDropRenderContextType {
    items: DragDropItemMap;
    textReplacement: TextReplacement;
}

type DragDropImageComponentResolver = (component: ComponentType) => Record<string, number>;

interface Props {
    items: DragDropItemMap;
    exclude?: Array<DragDropDefaultItems>;
    components: Array<ComponentType>;
    data: Record<string, unknown>;
    onReady?: () => void;
    getComponentImages?: DragDropImageComponentResolver;
}

function getImages(
    components: Array<ComponentType>,
    resolver: DragDropImageComponentResolver | undefined
): Record<string, number> {
    const results: Record<string, number> = {};

    for(const component of components) {
        if(component.type === "img") {
            const url = component.style.image;

            if(!url) {
                continue;
            }

            if(!results[url]) {
                results[url] = 0;
            }

            results[url]++;
        } else if(isColumnComponentType(component)) {
            for(const column of component.value) {
                const columnImages = getImages(column.components, resolver);

                for(const url in columnImages) {
                    if(!Object.prototype.hasOwnProperty.call(columnImages, url)) {
                        continue;
                    }

                    if(!results[url]) {
                        results[url] = 0;
                    }

                    results[url] += columnImages[url];
                }
            }
        } else if(typeof resolver === "function") {
            const componentImages = resolver(component);

            for(const url in componentImages) {
                if(!Object.prototype.hasOwnProperty.call(componentImages, url)) {
                    continue;
                }

                if(!results[url]) {
                    results[url] = 0;
                }

                results[url] += componentImages[url];
            }
        }
    }

    return results;
}

export const DragDropRenderContext = createContext<DragDropRenderContextType>(undefined!);
export const DragDropDataContext = createContext<Record<string, unknown>>({});

export const DragDropRenderer: FC<Props> = ({
    items: propItems,
    components,
    exclude,
    data,
    getComponentImages,
    onReady,
}) => {
    const items = useMemo(() => {
        const results: DragDropItemMap = { ...propItems };

        for(const key in DEFAULT_ITEMS) {
            if(Array.isArray(exclude) && exclude.includes(key)) {
                continue;
            }

            results[key] = DEFAULT_ITEMS[key];
        }

        return results;
    }, [ propItems, exclude ]);

    const textReplacement = useMemo(() => {
        return new TextReplacement((data?.text as Record<string, unknown>) ?? {});
    }, [ data ]);

    const context = useMemo(() => ({
        textReplacement,
        items,
    }), [ items, textReplacement ]);

    const imageCallback = useMemo((): undefined | DragDropImageLoadCallback => {
        if(typeof onReady !== "function") {
            return undefined;
        }

        const images = getImages(components, getComponentImages);

        let pending = 0;

        for(const url in images) {
            if(!Object.prototype.hasOwnProperty.call(images, url)) {
                continue;
            }

            pending += images[url];
        }

        // Ensure a max wait time of 1 second
        const timeout = setTimeout(onReady, 1000);

        return (url): void => {
            if(!pending) {
                return;
            }

            if(images[url]) {
                images[url]--;
                pending--;
            }

            if(!pending) {
                clearTimeout(timeout);
                onReady();
            }
        };
    }, [ components, onReady, getComponentImages ]);

    return (
        <DragDropRenderContext.Provider value={context}>
            <DragDropDataContext.Provider value={data}>
                <DragDropImageLoadContext.Provider value={imageCallback}>
                    {components.map((component, index) => {
                        const definition = items[component.type];

                        if(!definition) {
                            return null;
                        }

                        const Component = definition.component;

                        return (
                            <Component
                                key={index}
                                value={component.value}
                                style={component.style}
                            />
                        );
                    })}
                </DragDropImageLoadContext.Provider>
            </DragDropDataContext.Provider>
        </DragDropRenderContext.Provider>
    );
};
